<template>
  <div class="account-page py-4 mt-4">
    <div class="container">
      <div class="w-full main-card px-3 md:px-4 lg:px-5 py-4 lg:py-5 xl:py-7">
        <div class="flex flex-wrap items-center justify-between gap-4">
          <h2>Monthly statistics</h2>
          <div class="flex items-center gap-3">
            <date-picker picker="year" :allowClear="false" name="year" v-model:value="year" />
            <Button
              type="primary"
              class="btn-gradient"
              :loading="isLoadingStats"
              @click="loadStats"
            >
              {{ statsLoaded ? 'Refresh statistics' : 'Load statistics' }}
            </Button>
          </div>
        </div>
        <div class="stats-grid" v-if="statsLoaded && !isLoadingStats && !statsFailed">
          <!-- Total Users Card -->
          <StatCard
            title="Website visits"
            :value="homeStatsMonthQueryData?.web?.toLocaleString() ?? 0"
          />
          <StatCard
            title="Mobile visits"
            :value="homeStatsMonthQueryData?.mobile?.toLocaleString() ?? 0"
          />
          <StatCard
            title="Total trips"
            :value="homeStatsMonthQueryData?.total_trips?.toLocaleString() ?? 0"
          />
          <StatCard
            title="Total Distance (miles)"
            :value="formatBigIntNumber(homeStatsMonthQueryData?.total_miles?.toFixed(0) ?? 0)"
          />
          <StatCard
            title="Total emission reduction (tons CO2)"
            :value="formatBigIntNumber(homeStatsMonthQueryData?.total_emissions?.toFixed(0) ?? 0)"
          />
          <StatCard
            title="Total employers"
            :value="homeStatsMonthQueryData?.total_employers?.toLocaleString() ?? 0"
          />
          <StatCard
            title="Total Number of Active Users"
            :value="homeStatsMonthQueryData?.total_commuters?.toLocaleString() ?? 0"
          />
          <StatCard
            title="Total new commuters"
            :value="homeStatsMonthQueryData?.total_new_commuters?.toLocaleString() ?? 0"
          />
          <StatCard
            title="Total redeemed this month"
            :value="homeStatsMonthQueryData?.total_redeemed_this_month?.toLocaleString() ?? 0"
          />
          <StatCard
            title="Total commuters’ credit"
            :value="homeStatsMonthQueryData?.total_redeemed_today?.toLocaleString() ?? 0"
          />
        </div>
        <div v-if="isLoadingStats" class="flex items-center justify-center py-16">
          <Spin spinning size="large" />
        </div>
        <div v-if="statsFailed" class="flex items-center justify-center py-16 text-center">
          <p class="text-red-500 m-0">
            Couldn’t load the statistics for this month. Please try again.
          </p>
        </div>
        <div v-if="!statsLoaded" class="flex items-center justify-center py-16 text-center">
          <p class="text-gray-500 m-0">
            Monthly statistics are heavy to compute. Pick a month and year, then click
            <span class="font-bold">Load statistics</span>.
          </p>
        </div>
        <div class="mt-4 flex items-center gap-1 text-white">
          <span
            class="bg-primary-light hover:bg-primary h-7 px-4 rounded flex cursor-pointer items-center justify-center"
            @click="prevMonth"
            @keydown.enter="prevMonth"
          >
            <PhArrowLeft weight="bold" size="18" />
          </span>
          <span
            class="bg-primary-light h-7 flex-grow font-bold px-4 rounded flex items-center justify-center"
          >
            {{ monthNames[currentShowingMOnth - 1] }}
          </span>
          <span
            class="bg-primary-light hover:bg-primary h-7 px-4 rounded flex cursor-pointer items-center justify-center"
            @click="nextMonth"
            @keydown.enter="nextMonth"
          >
            <PhArrowRight weight="bold" size="18" />
          </span>
        </div>
      </div>
    </div>
  </div>
  <HomeStatsMonthQuery
    v-if="statsLoaded"
    :month="currentShowingMOnth"
    :key="`${currentShowingMOnth}-${monthStatYear}-${reloadToken}`"
    :year="monthStatYear"
  />
</template>
<script>
/* eslint-disable */
import HomeStatsQuery from '@/services/queries/admin/stats/HomeStatsQuery.vue';
import HomeStatsMonthQuery from '@/services/queries/admin/stats/HomeStatsMonthQuery.vue';
import StatCard from './StatCard.vue';
import { Button, DatePicker, Spin } from 'ant-design-vue';

import { PhArrowLeft, PhArrowRight } from 'phosphor-vue';
import dayjs from 'dayjs';
import { ADMIN_STATS } from '@/store/types';

export default {
  components: {
    StatCard,
    HomeStatsQuery,
    HomeStatsMonthQuery,
    Spin,
    PhArrowLeft,
    PhArrowRight,
    Button,
    DatePicker,
  },
  data() {
    return {
      monthBack: 0,
      year: dayjs(),
      statsLoaded: false,
      reloadToken: 0,
      monthNames: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ],
    };
  },
  created() {},
  watch: {
    // the query is expensive, so a new month/year waits for an explicit load
    statsPeriod() {
      this.statsLoaded = false;
      this.clearStats();
    },
  },
  methods: {
    clearStats() {
      this.$store.commit(ADMIN_STATS.SET_HOME_STATS_MONTH_QUERY, null);
    },
    loadStats() {
      if (this.isLoadingStats) return;
      if (this.statsLoaded) this.reloadToken += 1;
      this.clearStats();
      this.statsLoaded = true;
    },
    prevMonth() {
      this.monthBack += 1;
      if (this.currentShowingMOnth <= 0) this.monthBack = 0;
      return;
    },
    nextMonth() {
      this.monthBack -= 1;
      if (this.currentShowingMOnth > 12) this.monthBack = 0;
      return;
    },
    formatBigIntNumber(input) {
      const units = [
        { value: 1_000_000_000_000_000_000n, symbol: 'Q' }, // Quintillion
        { value: 1_000_000_000_000_000n, symbol: 'q' }, // Quadrillion
        { value: 1_000_000_000_000n, symbol: 'T' }, // Trillion
        { value: 1_000_000_000n, symbol: 'B' }, // Billion
        { value: 1_000_000n, symbol: 'M' }, // Million
        { value: 1_000n, symbol: 'K' }, // Thousand
      ];

      let number = typeof input === 'bigint' ? input : BigInt(input);

      for (const unit of units) {
        if (number >= unit.value) {
          const integerPart = number / unit.value;
          const decimalPart = ((number % unit.value) * 100n) / unit.value;
          return `${integerPart}.${decimalPart.toString().padStart(2, '0')} ${unit.symbol}`;
        }
      }

      return number.toLocaleString(); // return as-is if smaller than 1K
    },
    setInput(val) {
      if (val.y && val.y.value) {
        console.log('year === >', val.year.value);
        this.year = val.year.value;
      }
    },
  },
  computed: {
    homeStatsQueryData() {
      return this.$store.getters.homeStatsQueryData;
    },
    homeStatsQuery() {
      return this.$store.state.adminStats?.homeStatsQuery ?? null;
    },
    homeStatsMonthQueryData() {
      return this.$store.getters.homeStatsMonthQueryData;
    },
    homeStatsMonthQuery() {
      return this.$store.state.adminStats?.homeStatsMonthQuery ?? null;
    },
    currentShowingMOnth() {
      return new Date().getMonth() + 1 - this.monthBack;
    },
    monthStatYear() {
      return !this.year ? dayjs().get('year') : dayjs(this.year).get('year');
    },
    statsPeriod() {
      return `${this.currentShowingMOnth}-${this.monthStatYear}`;
    },
    statsFailed() {
      return this.statsLoaded && Boolean(this.homeStatsMonthQuery?.isError);
    },
    isLoadingStats() {
      if (!this.statsLoaded || this.statsFailed) return false;
      if (this.homeStatsMonthQuery?.isFetching || this.homeStatsMonthQuery?.isLoading) return true;
      // the query mounts one tick after the click, so treat "no data yet" as loading
      return !this.homeStatsMonthQueryData;
    },
  },
};
</script>
<style>
.title {
  font-size: 24px;
  font-weight: bold;
  color: #111827;
  margin-bottom: 24px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
}

.stats-card {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  transition: box-shadow 0.2s ease;
}

.stats-card:hover {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.card-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.card-info {
  flex: 1;
}

.card-title {
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 4px;
}

.card-value {
  font-size: 30px;
  font-weight: bold;
  color: #111827;
  margin-bottom: 8px;
}

.card-change {
  display: flex;
  align-items: center;
  gap: 4px;
}

.change-value {
  font-size: 14px;
  font-weight: 500;
}

.change-positive {
  color: #059669;
}

.change-negative {
  color: #dc2626;
}

.change-label {
  font-size: 12px;
  color: #6b7280;
}

.card-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.icon-blue {
  background-color: #dbeafe;
  color: #2563eb;
}

.icon-green {
  background-color: #d1fae5;
  color: #059669;
}

.icon-purple {
  background-color: #e9d5ff;
  color: #7c3aed;
}

.icon-orange {
  background-color: #fed7aa;
  color: #ea580c;
}

.icon {
  width: 24px;
  height: 24px;
  stroke: currentColor;
  stroke-width: 2;
  fill: none;
}

@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }

  body {
    padding: 16px;
  }
}
</style>
